// web/src/components/Pitch.tsx
// SVG pitch with two responsibilities:
//   1. <PitchPicker>  — tap-to-pick an (x, y) location and event type. Used
//      by the live match recorder and the match-edit screen to log events
//      with a pitch location.
//   2. <PitchHeatMap> — render a set of existing events as dots/density
//      over the same pitch background. Used by match-detail and the Stats
//      screen.
//
// Coordinate system, locked across the app:
//   x: 0 = left touchline, 100 = right touchline
//   y: 0 = own goal line,  100 = opposition goal line
//   The kid's team always attacks UP the page (y increases toward the
//   opposition goal). This avoids "we attack right" vs "they attack left"
//   ambiguity — the orientation is fixed for both heat-maps and pickers.
//
// We render an SVG with a viewBox of 0 0 100 150 so the pitch has a
// 2:3-ish portrait aspect ratio (close to real-pitch proportions and
// readable on phone screens). The mouse/touch handler converts screen
// coords back to pitch coords using getBoundingClientRect, so taps land
// exactly where the user pointed regardless of layout size.

import { useRef, useState } from 'react';
import type { MatchEvent, MatchEventType } from '../types';

// ── Constants ─────────────────────────────────────────────────────────────
// SVG viewBox dimensions. Pitch is 100 wide × 150 tall in pitch-coords.
// y still uses 0..100 in stored data — we just render with a vertical
// stretch so the pitch feels like an actual pitch rather than a square.
const VB_W = 100;
const VB_H = 150;

// Map stored y (0..100) into viewBox y (0..150). Stored 0 (own goal) at the
// BOTTOM of the SVG, stored 100 (opp goal) at the TOP — i.e. the kid
// attacks upward, matching "your kid's team attacks up the page".
function toViewY(y: number): number {
  return VB_H * (1 - y / 100);
}
function fromViewY(svgY: number): number {
  return Math.max(0, Math.min(100, (1 - svgY / VB_H) * 100));
}
function toViewX(x: number): number {
  return x; // 0..100 → 0..100, no transform
}
function fromViewX(svgX: number): number {
  return Math.max(0, Math.min(100, svgX));
}

// ── Static pitch markings (drawn once, shared by both modes) ─────────────
//
// Visual mimic of kickstart-Mobile's Pitch.js (2026-05-19): horizontal
// grass stripes alternating between two greens (14 bands), a low-opacity
// diagonal mowing pattern overlaid, a top/bottom depth gradient, and full
// pitch markings (penalty arc D-shapes, corner arcs, penalty spots).
//
// Kept FLAT (no rotateX perspective) so the tap → (x, y) math in the
// picker stays straightforward — perspective transform would force inverse
// projection on every tap, fragile across device pixel ratios.
function PitchMarkings() {
  const LINE = 'rgba(250,245,236,0.92)';
  const LINE_W = 0.7;

  // 14 alternating-shade horizontal grass stripes. Two greens picked from
  // the kickstart palette (#3a8b3f / #358538) — readable against cream
  // markings and on-brand with var(--green).
  const STRIPES = 14;
  const STRIPE_H = VB_H / STRIPES;

  return (
    <g>
      <defs>
        {/* Mowing diagonal pattern — low-opacity 45° stripes, tiled */}
        <pattern
          id="mfj-pitch-mowing"
          patternUnits="userSpaceOnUse"
          width={28}
          height={28}
          patternTransform="rotate(45)"
        >
          <rect width={14} height={28} fill="rgba(255,255,255,0.025)" />
        </pattern>
        {/* Top/bottom depth gradient — darker at goal lines, lighter in the
            middle. Gives the surface a quiet sense of vertical depth. */}
        <linearGradient id="mfj-pitch-depth" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="rgba(0,0,0,0.18)" />
          <stop offset="22%" stopColor="rgba(0,0,0,0.04)" />
          <stop offset="50%" stopColor="rgba(0,0,0,0)" />
          <stop offset="78%" stopColor="rgba(0,0,0,0.04)" />
          <stop offset="100%" stopColor="rgba(0,0,0,0.18)" />
        </linearGradient>
      </defs>

      {/* Base grass — 14 horizontal stripes */}
      {Array.from({ length: STRIPES }, (_, i) => (
        <rect
          key={`stripe-${i}`}
          x={0}
          y={i * STRIPE_H}
          width={VB_W}
          height={STRIPE_H + 0.1 /* tiny overlap to avoid sub-px hairlines */}
          fill={i % 2 === 0 ? '#3a8b3f' : '#358538'}
        />
      ))}
      {/* Mowing diagonal overlay */}
      <rect x={0} y={0} width={VB_W} height={VB_H} fill="url(#mfj-pitch-mowing)" />
      {/* Vertical depth gradient */}
      <rect x={0} y={0} width={VB_W} height={VB_H} fill="url(#mfj-pitch-depth)" />

      {/* ─── Field markings (cream, on top of grass) ───────────────────────
          All lines pointer-events: none so taps fall through to the SVG
          handler — only the surface itself catches clicks. */}
      <g pointerEvents="none" stroke={LINE} strokeWidth={LINE_W} fill="none">
        {/* Outer touchline */}
        <rect x={1} y={1} width={VB_W - 2} height={VB_H - 2} />
        {/* Halfway line */}
        <line x1={1} x2={VB_W - 1} y1={VB_H / 2} y2={VB_H / 2} />
        {/* Centre circle */}
        <circle cx={VB_W / 2} cy={VB_H / 2} r={9} />

        {/* TOP — opposition goal end */}
        <rect x={(VB_W - 44) / 2} y={1} width={44} height={16} />
        <rect x={(VB_W - 22) / 2} y={1} width={22} height={6} />
        {/* Penalty arc — D-shape outside the penalty box, centred on the
            penalty spot (50, 11), radius 8. Endpoints where the arc meets
            the penalty-box bottom edge at y=17. */}
        <path d="M 45 17 A 8 8 0 0 0 55 17" />

        {/* BOTTOM — own goal end (mirror) */}
        <rect x={(VB_W - 44) / 2} y={VB_H - 17} width={44} height={16} />
        <rect x={(VB_W - 22) / 2} y={VB_H - 7} width={22} height={6} />
        <path d={`M 45 ${VB_H - 17} A 8 8 0 0 1 55 ${VB_H - 17}`} />

        {/* Corner arcs — small quarter-circles curving INTO the field */}
        <path d="M 4 1 A 3 3 0 0 1 1 4" />
        <path d={`M ${VB_W - 4} 1 A 3 3 0 0 0 ${VB_W - 1} 4`} />
        <path d={`M 1 ${VB_H - 4} A 3 3 0 0 0 4 ${VB_H - 1}`} />
        <path d={`M ${VB_W - 1} ${VB_H - 4} A 3 3 0 0 1 ${VB_W - 4} ${VB_H - 1}`} />
      </g>

      {/* Spots — filled dots that need fill set so split from the lines group */}
      <g pointerEvents="none" fill={LINE}>
        <circle cx={VB_W / 2} cy={VB_H / 2} r={0.9} />
        <circle cx={VB_W / 2} cy={11} r={0.7} />
        <circle cx={VB_W / 2} cy={VB_H - 11} r={0.7} />
      </g>
    </g>
  );
}

// ── End-zone labels (so the operator knows which way is up) ───────────────
function PitchLabels() {
  return (
    <g aria-hidden>
      <text
        x={VB_W / 2}
        y={5}
        textAnchor="middle"
        fontSize={3.2}
        fill="rgba(250,245,236,0.55)"
        fontFamily="var(--font-body)"
        style={{ letterSpacing: '0.18em', textTransform: 'uppercase' }}
      >
        Opposition goal
      </text>
      <text
        x={VB_W / 2}
        y={VB_H - 2.2}
        textAnchor="middle"
        fontSize={3.2}
        fill="rgba(250,245,236,0.55)"
        fontFamily="var(--font-body)"
        style={{ letterSpacing: '0.18em', textTransform: 'uppercase' }}
      >
        Your goal
      </text>
    </g>
  );
}

// ─────────────────────────────────────────────────────────────────────────
//                              PITCH PICKER
// ─────────────────────────────────────────────────────────────────────────

interface PickerProps {
  /**
   * Called when the user has chosen a location AND an event type — i.e.
   * the full event is ready to commit. Coordinates are normalised 0..100
   * with one decimal of precision.
   *
   * `minute` and `note` are captured in the second modal step (2026-05-19).
   * If the user skips that step they're undefined and the consumer should
   * auto-expand the row for the parent to fill in later — same behaviour
   * as before the two-step modal landed.
   */
  onPick: (event: {
    x: number;
    y: number;
    type: MatchEventType;
    minute?: number;
    note?: string;
  }) => void;
  /** Disables interaction (e.g. while saving). */
  disabled?: boolean;
  /**
   * Optional — match length in minutes. Used to drive quick-pick chips
   * for HT/FT in the minute-entry step. Not required.
   */
  matchLengthMin?: number;
  /**
   * Already-committed events for this match (2026-05-20). Rendered as
   * small persistent dots on the pitch so the parent can see what
   * they've already logged while choosing where the next event happened.
   * Events without (x,y) are ignored. Dots are pointer-events:none so
   * they don't steal taps from the SVG handler.
   *
   * Typed loosely (just the fields we render) so the consumer can pass
   * a draft-event shape with `minute: string` etc. without conversion.
   */
  recordedEvents?: ReadonlyArray<{
    type: MatchEventType;
    x?: number;
    y?: number;
  }>;
}

const EVENT_OPTIONS: { type: MatchEventType; label: string }[] = [
  { type: 'goal', label: 'Goal' },
  { type: 'assist', label: 'Assist' },
  { type: 'shot_on', label: 'Shot on' },
  { type: 'shot_off', label: 'Shot off' },
  { type: 'save', label: 'Save' },
  { type: 'defensive', label: 'Defensive' },
];

export function PitchPicker({
  onPick,
  disabled = false,
  matchLengthMin,
  recordedEvents,
}: PickerProps) {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const [pending, setPending] = useState<{ x: number; y: number } | null>(null);
  // Two-step modal state (2026-05-19): once the user picks an event type
  // we don't commit immediately — we show a minute + optional note step
  // so the parent captures the timing right at the moment of the tap.
  // Solves the long-running "minute=0 in DDB" problem caused by users
  // forgetting to fill the row after the modal closed.
  const [pickedType, setPickedType] = useState<MatchEventType | null>(null);
  const [minute, setMinute] = useState('');
  const [noteText, setNoteText] = useState('');

  const closeAll = () => {
    setPending(null);
    setPickedType(null);
    setMinute('');
    setNoteText('');
  };

  const onTap = (e: React.MouseEvent<SVGSVGElement>) => {
    if (disabled) return;
    const svg = svgRef.current;
    if (!svg) return;
    const rect = svg.getBoundingClientRect();
    // Convert client coords → SVG viewBox coords. The viewBox is 100 × 150
    // and the SVG element has a real CSS size; cross-multiply.
    const cssX = e.clientX - rect.left;
    const cssY = e.clientY - rect.top;
    const vbX = (cssX / rect.width) * VB_W;
    const vbY = (cssY / rect.height) * VB_H;
    setPending({
      x: Math.round(fromViewX(vbX) * 10) / 10,
      y: Math.round(fromViewY(vbY) * 10) / 10,
    });
    // Reset step-2 state when picking a new location.
    setPickedType(null);
    setMinute('');
    setNoteText('');
  };

  // Step 1: chose an event type → transition to step 2 (minute + note).
  const chooseType = (type: MatchEventType) => {
    if (!pending) return;
    setPickedType(type);
  };

  // Step 2: commit with minute + (optional) note.
  const commitFull = () => {
    if (!pending || !pickedType) return;
    const min = minute === '' ? undefined : Number(minute);
    if (min !== undefined && (!Number.isInteger(min) || min < 0)) return;
    const trimmedNote = noteText.trim();
    onPick({
      x: pending.x,
      y: pending.y,
      type: pickedType,
      minute: min,
      note: trimmedNote.length > 0 ? trimmedNote : undefined,
    });
    closeAll();
  };

  // "Skip — fill later" — keep the old behaviour where the row auto-
  // expands for the parent to fill in. Useful during fast recording.
  const skipMinute = () => {
    if (!pending || !pickedType) return;
    onPick({ x: pending.x, y: pending.y, type: pickedType });
    closeAll();
  };

  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <svg
        ref={svgRef}
        viewBox={`0 0 ${VB_W} ${VB_H}`}
        preserveAspectRatio="xMidYMid meet"
        onClick={onTap}
        role="button"
        aria-label="Tap the pitch to log an event location"
        style={{
          display: 'block',
          width: '100%',
          height: 'auto',
          maxHeight: '60vh',
          borderRadius: 12,
          cursor: disabled ? 'not-allowed' : 'crosshair',
          touchAction: 'manipulation',
          userSelect: 'none',
          // Soft drop shadow under the pitch — adds the "field sits on
          // a surface" feel that kickstart's <fieldShadow> achieved with
          // a positioned div. Box-shadow is cheaper + crisper.
          boxShadow:
            '0 10px 24px -8px rgba(0,0,0,0.35), ' +
            '0 4px 8px -2px rgba(0,0,0,0.18), ' +
            'inset 0 0 0 1px rgba(0,0,0,0.08)',
        }}
      >
        <PitchMarkings />
        <PitchLabels />

        {/* Persistent dots — every already-committed event with (x,y)
            stays on the pitch surface so the parent can see what they've
            logged while choosing the next tap (2026-05-20). Coloured by
            event type using the same swatch we use in <PitchHeatMap>.
            pointer-events:none so they don't intercept taps. */}
        {recordedEvents && recordedEvents.length > 0 ? (
          <g pointerEvents="none">
            {recordedEvents
              .filter((e) => typeof e.x === 'number' && typeof e.y === 'number')
              .map((e, i) => (
                <g key={`recorded-${i}`}>
                  <circle
                    cx={toViewX(e.x!)}
                    cy={toViewY(e.y!)}
                    r={2.4}
                    fill={TYPE_COLOR[e.type]}
                    stroke="var(--ink-on-green)"
                    strokeWidth={0.4}
                    opacity={0.9}
                  />
                </g>
              ))}
          </g>
        ) : null}

        {/* Pending dot — visible until the user either chooses an event type
            or taps elsewhere (which moves the dot). Rendered LAST so it
            sits on top of the persistent dots. */}
        {pending ? (
          <g pointerEvents="none">
            <circle
              cx={toViewX(pending.x)}
              cy={toViewY(pending.y)}
              r={2.8}
              fill="var(--gold)"
              stroke="var(--ink-on-green)"
              strokeWidth={0.6}
            />
            <circle
              cx={toViewX(pending.x)}
              cy={toViewY(pending.y)}
              r={5.5}
              fill="none"
              stroke="var(--gold)"
              strokeOpacity={0.4}
              strokeWidth={0.8}
            />
          </g>
        ) : null}
      </svg>

      <p
        style={{
          margin: '8px 0 0',
          fontSize: 'var(--small)',
          color: 'var(--muted)',
          textAlign: 'center',
        }}
      >
        Tap the pitch where the action happened.
      </p>

      {/* Event picker — full-screen modal with two steps (2026-05-19):
          Step 1: pick event type (Goal / Assist / ...).
          Step 2: enter minute + optional one-line note → Save event.
          The pending pitch dot stays visible on the pitch underneath; the
          modal overlay dims everything else. */}
      {pending ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-label={pickedType ? 'When did it happen' : 'Choose event type'}
          onClick={closeAll}
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 1500,
            background: 'rgba(20, 24, 26, 0.55)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: 'min(420px, 100%)',
              background: 'var(--surface)',
              borderRadius: 16,
              padding: 'var(--space-lg)',
              boxShadow: '0 18px 48px rgba(0,0,0,0.25)',
              position: 'relative',
            }}
          >
            <button
              type="button"
              onClick={closeAll}
              aria-label="Cancel"
              style={{
                position: 'absolute',
                top: 12,
                right: 12,
                width: 32,
                height: 32,
                border: 'none',
                borderRadius: '50%',
                background: 'transparent',
                color: 'var(--muted)',
                cursor: 'pointer',
                fontSize: 18,
              }}
            >
              ✕
            </button>

            <div
              style={{
                fontSize: 'var(--micro)',
                letterSpacing: '0.22em',
                textTransform: 'uppercase',
                color: 'var(--gold)',
                marginBottom: 6,
                fontWeight: 700,
              }}
            >
              {pickedType
                ? `${EVENT_OPTIONS.find((o) => o.type === pickedType)?.label ?? pickedType} at ${Math.round(pending.x)}, ${Math.round(pending.y)}`
                : `At ${Math.round(pending.x)}, ${Math.round(pending.y)} on the pitch`}
            </div>
            <h2
              style={{
                fontFamily: 'var(--font-masthead)',
                fontStyle: 'italic',
                textTransform: 'uppercase',
                letterSpacing: '-0.01em',
                fontSize: '1.6rem',
                lineHeight: 1.05,
                margin: '0 0 16px',
              }}
            >
              {pickedType ? 'When did it happen?' : 'What happened?'}
            </h2>

            {!pickedType ? (
              // ─── Step 1: pick an event type ────────────────────────────
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(2, 1fr)',
                  gap: 10,
                }}
              >
                {EVENT_OPTIONS.map((opt) => (
                  <button
                    key={opt.type}
                    type="button"
                    onClick={() => chooseType(opt.type)}
                    disabled={disabled}
                    style={{
                      padding: '16px 10px',
                      minHeight: 56,
                      borderRadius: 12,
                      border: 'none',
                      background: 'var(--green)',
                      color: 'var(--ink-on-green)',
                      fontFamily: 'var(--font-body)',
                      fontSize: 15,
                      fontWeight: 600,
                      cursor: disabled ? 'not-allowed' : 'pointer',
                      textTransform: 'uppercase',
                      letterSpacing: '0.04em',
                    }}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            ) : (
              // ─── Step 2: minute + optional note + Save ────────────────
              <div>
                <label
                  htmlFor="mfj-event-minute"
                  style={{
                    display: 'block',
                    fontSize: 'var(--small)',
                    color: 'var(--muted)',
                    marginBottom: 6,
                    fontWeight: 600,
                  }}
                >
                  Minute
                </label>
                <input
                  id="mfj-event-minute"
                  // The single most important field on this modal — autofocus
                  // it so the parent just starts typing the number.
                  autoFocus
                  type="number"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  min={0}
                  max={matchLengthMin ? matchLengthMin + 30 : undefined}
                  value={minute}
                  onChange={(e) => setMinute(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      commitFull();
                    }
                  }}
                  placeholder="e.g. 23"
                  disabled={disabled}
                  style={{
                    width: '100%',
                    padding: '14px 14px',
                    fontSize: 20,
                    fontWeight: 600,
                    borderRadius: 10,
                    border: '1px solid var(--divider)',
                    background: 'var(--bg)',
                    color: 'var(--ink)',
                    boxSizing: 'border-box',
                    marginBottom: 4,
                  }}
                />
                {matchLengthMin ? (
                  <div
                    style={{
                      fontSize: 'var(--micro)',
                      color: 'var(--muted)',
                      marginBottom: 12,
                    }}
                  >
                    Halves are {Math.floor(matchLengthMin / 2)} min each. After {matchLengthMin}' = extra time.
                  </div>
                ) : (
                  <div style={{ marginBottom: 12 }} />
                )}

                <label
                  htmlFor="mfj-event-note"
                  style={{
                    display: 'block',
                    fontSize: 'var(--small)',
                    color: 'var(--muted)',
                    marginBottom: 6,
                    fontWeight: 600,
                  }}
                >
                  Note <span style={{ fontWeight: 400 }}>(optional, one line)</span>
                </label>
                <input
                  id="mfj-event-note"
                  type="text"
                  maxLength={120}
                  value={noteText}
                  onChange={(e) => setNoteText(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      commitFull();
                    }
                  }}
                  placeholder="e.g. header from a corner"
                  disabled={disabled}
                  style={{
                    width: '100%',
                    padding: '12px 14px',
                    fontSize: 15,
                    borderRadius: 10,
                    border: '1px solid var(--divider)',
                    background: 'var(--bg)',
                    color: 'var(--ink)',
                    boxSizing: 'border-box',
                    marginBottom: 16,
                  }}
                />

                <div
                  style={{
                    display: 'flex',
                    gap: 10,
                    alignItems: 'center',
                  }}
                >
                  <button
                    type="button"
                    onClick={() => setPickedType(null)}
                    disabled={disabled}
                    style={{
                      padding: '12px 16px',
                      minHeight: 48,
                      borderRadius: 10,
                      border: '1px solid var(--divider)',
                      background: 'transparent',
                      color: 'var(--ink)',
                      fontFamily: 'var(--font-body)',
                      fontSize: 14,
                      fontWeight: 600,
                      cursor: disabled ? 'not-allowed' : 'pointer',
                    }}
                  >
                    ← Back
                  </button>
                  <button
                    type="button"
                    onClick={commitFull}
                    disabled={disabled}
                    style={{
                      flex: 1,
                      padding: '14px 16px',
                      minHeight: 48,
                      borderRadius: 10,
                      border: 'none',
                      background: 'var(--green)',
                      color: 'var(--ink-on-green)',
                      fontFamily: 'var(--font-body)',
                      fontSize: 15,
                      fontWeight: 700,
                      cursor: disabled ? 'not-allowed' : 'pointer',
                      textTransform: 'uppercase',
                      letterSpacing: '0.04em',
                    }}
                  >
                    Save event
                  </button>
                </div>
                <div style={{ textAlign: 'center', marginTop: 10 }}>
                  <button
                    type="button"
                    onClick={skipMinute}
                    disabled={disabled}
                    style={{
                      background: 'transparent',
                      border: 'none',
                      color: 'var(--muted)',
                      fontSize: 'var(--small)',
                      cursor: disabled ? 'not-allowed' : 'pointer',
                      textDecoration: 'underline',
                      textUnderlineOffset: 2,
                    }}
                  >
                    Skip — fill later
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      ) : null}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
//                              HEAT-MAP
// ─────────────────────────────────────────────────────────────────────────

interface HeatMapProps {
  events: MatchEvent[];
  /**
   * Optional filter — only render events of these types. Undefined = all.
   * Lets callers compose a "Goals only" view without copying the data.
   */
  filter?: ReadonlySet<MatchEventType>;
  /**
   * Max viewport height. Defaults to '60vh' — matches the picker.
   */
  maxHeight?: string;
}

// Per-event-type colour. Matches the swatch-color convention used elsewhere
// (var(--accent-coral) for goals, var(--gold) for MoM-adjacent, etc).
const TYPE_COLOR: Record<MatchEventType, string> = {
  goal: 'var(--accent-coral)',
  assist: 'var(--gold-light)',
  shot_on: 'var(--electric-yellow)',
  shot_off: 'rgba(250,245,236,0.55)',
  save: '#7DC2FF',
  defensive: 'var(--surface-2)',
};

export function PitchHeatMap({ events, filter, maxHeight = '60vh' }: HeatMapProps) {
  // Filter to events with coordinates; further by type if a filter is set.
  const dots = events.filter((e) => {
    if (typeof e.x !== 'number' || typeof e.y !== 'number') return false;
    if (filter && !filter.has(e.type)) return false;
    return true;
  });

  return (
    <div style={{ width: '100%' }}>
      <svg
        viewBox={`0 0 ${VB_W} ${VB_H}`}
        preserveAspectRatio="xMidYMid meet"
        style={{
          display: 'block',
          width: '100%',
          height: 'auto',
          maxHeight,
          borderRadius: 12,
          // Match the PitchPicker's "field sits on a surface" shadow so
          // the two pitch surfaces feel like the same thing.
          boxShadow:
            '0 10px 24px -8px rgba(0,0,0,0.35), ' +
            '0 4px 8px -2px rgba(0,0,0,0.18), ' +
            'inset 0 0 0 1px rgba(0,0,0,0.08)',
        }}
        aria-label={`${dots.length} located event${dots.length === 1 ? '' : 's'}`}
      >
        <PitchMarkings />
        <PitchLabels />

        {dots.map((e, i) => (
          <g key={i}>
            <circle
              cx={toViewX(e.x!)}
              cy={toViewY(e.y!)}
              r={3.2}
              fill={TYPE_COLOR[e.type]}
              stroke="var(--ink-on-green)"
              strokeWidth={0.4}
              opacity={0.85}
            />
          </g>
        ))}
      </svg>

      {dots.length === 0 ? (
        <p
          style={{
            margin: '8px 0 0',
            fontSize: 'var(--small)',
            color: 'var(--muted)',
            textAlign: 'center',
          }}
        >
          No located events on this match yet.
        </p>
      ) : (
        <p
          style={{
            margin: '8px 0 0',
            fontSize: 'var(--small)',
            color: 'var(--muted)',
            textAlign: 'center',
          }}
        >
          {dots.length} of {events.length} events located on the pitch.
        </p>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────
//                         DENSITY HEAT-MAP (season)
// ─────────────────────────────────────────────────────────────────────────
//
// Smooth blob-style density visualisation — the broadcast/Opta look (cool
// blue periphery → green → amber → hot red core). Designed for showing
// where a kid has been active across an entire season's worth of matches,
// where the dots-style heat-map would be too cluttered.
//
// Technique: four stacked SVG layers, each rendering all event positions
// as filled circles, each with a different (radius, blur, colour, opacity)
// tuple. Where many events overlap, all four layers stack → red dominates.
// Where a single isolated event sits, only the blue periphery layer is
// visually present. This approximates a Gaussian KDE without needing a
// canvas pass — keeps the implementation pure SVG, composable with the
// same <PitchMarkings>/<PitchLabels> we use elsewhere, and lets the React
// render flow stay declarative.

interface DensityHeatMapProps {
  /** Every event from across a kid's matches. Events without (x,y) are
   *  filtered out — same convention as <PitchHeatMap>. */
  events: MatchEvent[];
  /** Optional cap on rendered SVG height. Defaults to 60vh. */
  maxHeight?: string;
}

/**
 * Layer descriptors for the blur-stack technique. Order matters: cooler
 * + wider layers paint first (back), hot core paints last (front). The
 * `idSuffix` lets us namespace the SVG <filter> ids so this component
 * can render twice on the same page without filter clashes.
 */
const DENSITY_LAYERS = [
  { color: '#2f5fd8', radius: 11, stdDev: 7.5, opacity: 0.32, idSuffix: 'far' },
  { color: '#3fb24a', radius: 7.5, stdDev: 5, opacity: 0.46, idSuffix: 'mid' },
  { color: '#f0c020', radius: 4.5, stdDev: 3, opacity: 0.62, idSuffix: 'near' },
  { color: '#e23a2a', radius: 2.5, stdDev: 1.6, opacity: 0.86, idSuffix: 'core' },
] as const;

export function PitchDensityHeatMap({ events, maxHeight = '60vh' }: DensityHeatMapProps) {
  const dots = events.filter((e) => typeof e.x === 'number' && typeof e.y === 'number');

  // A stable per-instance id so two heat-maps on the same page don't
  // collide on their <filter> ids. Math.random would re-fire on render,
  // breaking the filter reference; useRef would be cleaner if this ever
  // animates, but events are stable-by-mount so a useMemo-equivalent
  // (computed once per call) is fine here.
  const uid = `mfj-density-${dots.length}-${dots[0]?.x ?? 0}-${dots[0]?.y ?? 0}`;

  return (
    <div style={{ width: '100%' }}>
      <svg
        viewBox={`0 0 ${VB_W} ${VB_H}`}
        preserveAspectRatio="xMidYMid meet"
        style={{
          display: 'block',
          width: '100%',
          height: 'auto',
          maxHeight,
          borderRadius: 12,
          // Same "field sits on a surface" shadow as the other pitch
          // surfaces — keeps the visual family consistent.
          boxShadow:
            '0 10px 24px -8px rgba(0,0,0,0.35), ' +
            '0 4px 8px -2px rgba(0,0,0,0.18), ' +
            'inset 0 0 0 1px rgba(0,0,0,0.08)',
        }}
        aria-label={`Season density heat-map: ${dots.length} located event${dots.length === 1 ? '' : 's'}`}
      >
        <defs>
          {DENSITY_LAYERS.map((layer) => (
            <filter
              key={layer.idSuffix}
              id={`${uid}-${layer.idSuffix}`}
              // Generous filter region so the blurred halo isn't clipped
              // at the pitch edges (default region clips to source bbox).
              x="-50%"
              y="-50%"
              width="200%"
              height="200%"
            >
              <feGaussianBlur stdDeviation={layer.stdDev} />
            </filter>
          ))}
        </defs>

        <PitchMarkings />
        <PitchLabels />

        {/* Density layers, back-to-front. The hot red core sits on top so
            the brightest signal wins where events cluster. */}
        {DENSITY_LAYERS.map((layer) => (
          <g
            key={layer.idSuffix}
            filter={`url(#${uid}-${layer.idSuffix})`}
            opacity={layer.opacity}
            pointerEvents="none"
          >
            {dots.map((e, i) => (
              <circle
                key={i}
                cx={toViewX(e.x!)}
                cy={toViewY(e.y!)}
                r={layer.radius}
                fill={layer.color}
              />
            ))}
          </g>
        ))}
      </svg>

      {dots.length === 0 ? (
        <p
          style={{
            margin: '8px 0 0',
            fontSize: 'var(--small)',
            color: 'var(--muted)',
            textAlign: 'center',
          }}
        >
          Tap the pitch when you record events to build a season heat-map.
        </p>
      ) : (
        <p
          style={{
            margin: '8px 0 0',
            fontSize: 'var(--small)',
            color: 'var(--muted)',
            textAlign: 'center',
          }}
        >
          {dots.length} located event{dots.length === 1 ? '' : 's'} across the season.
        </p>
      )}
    </div>
  );
}
